import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { StatsGrid } from "@/components/admin/StatsGrid";
import { TripsManager } from "@/components/admin/TripsManager";
import { EnquiriesManager } from "@/components/admin/EnquiriesManager";
import { ReviewsManager } from "@/components/admin/ReviewsManager";
import { PhotosManager } from "@/components/admin/PhotosManager";
import { PagesManager } from "@/components/admin/PagesManager";
import { PartnersManager } from "@/components/admin/PartnersManager";
import { ContentManager } from "@/components/admin/ContentManager";

const tab = "rounded-full px-5 py-2 text-cream/70 data-[state=active]:bg-cream data-[state=active]:text-ink";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <main className="min-h-screen bg-[#0F0C0A] text-cream" data-testid="admin-dashboard">
      <header className="border-b border-cream/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="admin-home-link"><Logo light /></Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:inline text-cream/70 font-mono">{user?.email}</span>
            <Link to="/" className="hidden sm:inline-flex items-center gap-1 text-cream/70 hover:text-cream"><ExternalLink className="h-4 w-4" /> Site</Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full border border-cream/20 px-4 py-2 hover:bg-cream hover:text-ink transition-colors">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Dashboard</h1>
        <Tabs defaultValue="overview" className="mt-8">
          <TabsList className="bg-cream/5 rounded-full p-1 h-auto flex-wrap">
            <TabsTrigger value="overview" className={tab} data-testid="admin-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="trips" className={tab} data-testid="admin-tab-trips">Trips</TabsTrigger>
            <TabsTrigger value="enquiries" className={tab} data-testid="admin-tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="reviews" className={tab} data-testid="admin-tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="photos" className={tab} data-testid="admin-tab-photos">Country Photos</TabsTrigger>
            <TabsTrigger value="content" className={tab} data-testid="admin-tab-content">Website Text</TabsTrigger>
            <TabsTrigger value="pages" className={tab} data-testid="admin-tab-pages">Pages</TabsTrigger>
            <TabsTrigger value="partners" className={tab} data-testid="admin-tab-partners">Partners</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-8"><StatsGrid stats={stats} /></TabsContent>
          <TabsContent value="trips" className="mt-8"><TripsManager onChange={loadStats} /></TabsContent>
          <TabsContent value="enquiries" className="mt-8"><EnquiriesManager onChange={loadStats} /></TabsContent>
          <TabsContent value="reviews" className="mt-8"><ReviewsManager /></TabsContent>
          <TabsContent value="photos" className="mt-8"><PhotosManager /></TabsContent>
          <TabsContent value="content" className="mt-8"><ContentManager /></TabsContent>
          <TabsContent value="pages" className="mt-8"><PagesManager /></TabsContent>
          <TabsContent value="partners" className="mt-8"><PartnersManager /></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
